import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Tag, Button, Space, message, Typography, Descriptions,
  Input, Modal, Form, Select, InputNumber, Switch, Popconfirm, Divider, Alert, Tooltip,
} from 'antd';
import {
  ApiOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LinkOutlined,
  SettingOutlined,
  VideoCameraOutlined,
  PictureOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  SaveOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { adminAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;

// 默认模型列表（对齐 XRToken 上游实际提供的视频/图片模型）
const DEFAULT_MODELS = [
  // ========== 视频模型（per_token 计费，input_price = $/百万tokens，720p 价格） ==========
  { model_name: 'seedance-2.0', display_name: 'Seedance 2.0', model_type: 'video', upstream_model_name: 'dreamina-seedance-2-0-260128', input_price: 7.0, output_price: 0, billing_type: 'per_token', billing_rate: 0, api_path: '/videos/generations', status: 'active', priority: 10, metadata: JSON.stringify({ version: '2.0', tier: '旗舰版', tierColor: '#ff4d4f', desc: 'Seedance 2.0 最新旗舰视频生成，支持多模态参考/首尾帧/智能多帧', features: ['1080p超清', '15秒时长', '3种生成模式', '有声视频'], resolutions: [{ value: '480p', label: '480p', disabled: false }, { value: '720p', label: '720p', disabled: false }, { value: '1080p', label: '1080p', disabled: false }], durationRange: [4, 15], fpsOptions: [24, 30], maxRefImages: 9, maxRefVideos: 3, maxRefAudio: 3 }) },
  { model_name: 'seedance-2.0-fast', display_name: 'Seedance 2.0 Fast', model_type: 'video', upstream_model_name: 'dreamina-seedance-2-0-fast-260128', input_price: 5.6, output_price: 0, billing_type: 'per_token', billing_rate: 0, api_path: '/videos/generations', status: 'active', priority: 9, metadata: JSON.stringify({ version: '2.0 Fast', tier: '快速版', tierColor: '#52c41a', desc: 'Seedance 2.0 Fast 快速视频生成，更快的出片速度', features: ['720p高清', '15秒时长', '快速出片'], resolutions: [{ value: '480p', label: '480p', disabled: false }, { value: '720p', label: '720p', disabled: false }], durationRange: [4, 15], fpsOptions: [24], maxRefImages: 9, maxRefVideos: 3, maxRefAudio: 3 }) },
  { model_name: 'seedance-2.5-idle', display_name: 'Seedance 2.5 Idle', model_type: 'video', upstream_model_name: 'doubao-seedance-2-5-idle-260628', input_price: 70.0, output_price: 0, billing_type: 'per_token', billing_rate: 0, api_path: '/v1/videos/generations', status: 'active', priority: 11, metadata: JSON.stringify({ version: '2.5 Idle', tier: '旗舰版', tierColor: '#ff4d4f', desc: 'Seedance 2.5 旗舰版, 30秒时长/50素材上限/有声视频', features: ['最长30秒', '50个多模态参考', '有声视频', 'mov格式'], resolution_pricing: { '480p': 70, '720p': 70, '720p_ref': 42 }, durationRange: [4, 30], maxRefImages: 30, maxRefVideos: 10, maxRefAudio: 10 }), transform_config: JSON.stringify({ request_transform: { body_template: { model: '{UPSTREAM_MODEL}', content: [{ type: 'text', text: '{prompt}' }], ratio: '{ratio}', duration: '{duration}', generate_audio: '{generate_audio}' } } }) },
  // ========== 图片模型（per_image 计费，billing_rate = $/张） ==========
  { model_name: 'seedream-4.0', display_name: 'Seedream 4.0', model_type: 'image', upstream_model_name: 'seedream-4-0-250828', input_price: 0, output_price: 0, billing_type: 'per_image', billing_rate: 0.03, api_path: '/v1/images/generations', status: 'active', priority: 10, metadata: JSON.stringify({ version: '4.0', tier: '旗舰版', tierColor: '#ff4d4f', desc: 'Seedream 4.0 旗舰图片生成，极致细节', features: ['2048px超高清', '多风格', '图生图'], sizes: [{ value: '1024x1024', label: '1024×1024 (1:1)', aspect: '1:1' }, { value: '1024x1536', label: '1024×1536 (2:3)', aspect: '2:3' }, { value: '1536x1024', label: '1536×1024 (3:2)', aspect: '3:2' }, { value: '2048x2048', label: '2048×2048 (1:1)', aspect: '1:1' }], maxImages: 4, styles: [{ value: 'photorealistic', label: '写实摄影', emoji: '📷' }, { value: 'illustration', label: '精美插画', emoji: '🎨' }, { value: 'anime', label: '二次元', emoji: '🎌' }, { value: 'oil-painting', label: '油画', emoji: '🖼️' }, { value: '3d-render', label: '3D渲染', emoji: '💎' }, { value: 'watercolor', label: '水彩', emoji: '🖌️' }, { value: 'sketch', label: '素描', emoji: '✏️' }, { value: 'pixel-art', label: '像素', emoji: '👾' }] }) },
  { model_name: 'seedream-4.5', display_name: 'Seedream 4.5', model_type: 'image', upstream_model_name: 'seedream-4-5-251128', input_price: 0, output_price: 0, billing_type: 'per_image', billing_rate: 0.04, api_path: '/v1/images/generations', status: 'active', priority: 9, metadata: JSON.stringify({ version: '4.5', tier: '专业版', tierColor: '#fa8c16', desc: 'Seedream 4.5 图片生成', features: ['2048px超高清', '多风格', '良好细节'], sizes: [{ value: '1024x1024', label: '1024×1024 (1:1)', aspect: '1:1' }, { value: '1024x1536', label: '1024×1536 (2:3)', aspect: '2:3' }, { value: '1536x1024', label: '1536×1024 (3:2)', aspect: '3:2' }, { value: '2048x2048', label: '2048×2048 (1:1)', aspect: '1:1' }], maxImages: 4, styles: [{ value: 'photorealistic', label: '写实摄影', emoji: '📷' }, { value: 'illustration', label: '精美插画', emoji: '🎨' }, { value: 'anime', label: '二次元', emoji: '🎌' }, { value: 'oil-painting', label: '油画', emoji: '🖼️' }, { value: '3d-render', label: '3D渲染', emoji: '💎' }] }) },
  { model_name: 'seedream-5.0-lite', display_name: 'Seedream 5.0 Lite', model_type: 'image', upstream_model_name: 'seedream-5-0-lite-260128', input_price: 0, output_price: 0, billing_type: 'per_image', billing_rate: 0.035, api_path: '/v1/images/generations', status: 'active', priority: 8, metadata: JSON.stringify({ version: '5.0 Lite', tier: '轻量版', tierColor: '#52c41a', desc: 'Seedream 5.0 Lite 快速图片生成，高性价比', features: ['2048px超高清', '快速出图', '高性价比'], sizes: [{ value: '1024x1024', label: '1024×1024 (1:1)', aspect: '1:1' }, { value: '1024x1536', label: '1024×1536 (2:3)', aspect: '2:3' }, { value: '1536x1024', label: '1536×1024 (3:2)', aspect: '3:2' }], maxImages: 4, styles: [{ value: 'photorealistic', label: '写实摄影', emoji: '📷' }, { value: 'illustration', label: '精美插画', emoji: '🎨' }, { value: 'anime', label: '二次元', emoji: '🎌' }] }) },
];

// 从模型的 metadata 字段解析特性信息
const parseModelMetadata = (model) => {
  if (!model?.metadata) {
    // 返回 null 表示使用默认模板填充
    return null;
  }
  try {
    const meta = typeof model.metadata === 'string' ? JSON.parse(model.metadata) : model.metadata;
    return {
      version: meta.version || '',
      tier: meta.tier || '',
      tierColor: meta.tierColor || 'default',
      desc: meta.desc || '',
      features: meta.features || [],
      resolutions: meta.resolutions || [],
      sizes: meta.sizes || [],
      maxImages: meta.maxImages || 0,
      maxRefImages: meta.maxRefImages || 0,
      styles: meta.styles || [],
      durationRange: meta.durationRange || [],
      fpsOptions: meta.fpsOptions || [],
    };
  } catch {
    return { tier: '', tierColor: 'default', desc: '', features: [] };
  }
};

// 从 metadata 获取展示用的特性标签
const getFeatureTags = (model) => {
  const meta = parseModelMetadata(model);
  const features = meta?.features || [];
  if (features.length === 0) return [];
  return features;
};

const XRToken = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // 供应商
  const [xrProvider, setXrProvider] = useState(null);
  const [allProviders, setAllProviders] = useState([]);

  // 模型
  const [allModels, setAllModels] = useState([]);
  const [seedanceModels, setSeedanceModels] = useState([]);
  const [seedreamModels, setSeedreamModels] = useState([]);
  const [formBillingType, setFormBillingType] = useState('per_token');

  // 从上游同步
  const [upstreamModalOpen, setUpstreamModalOpen] = useState(false);
  const [upstreamModels, setUpstreamModels] = useState([]);
  const [upstreamLoading, setUpstreamLoading] = useState(false);
  const [selectedUpstream, setSelectedUpstream] = useState([]);
  const [upstreamPrices, setUpstreamPrices] = useState({}); // model_id → price

  // Provider 编辑表单
  const [providerForm] = Form.useForm();
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [providerEditMode, setProviderEditMode] = useState(false);

  // Model 编辑表单
  const [modelForm] = Form.useForm();
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [modelEditMode, setModelEditMode] = useState(false);
  const [editingModel, setEditingModel] = useState(null);

  const [modelModalType, setModelModalType] = useState('video');

  // ========== 数据加载 ==========
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [provRes, modelRes] = await Promise.all([
        adminAPI.getProviders(),
        adminAPI.getModelConfigs(),
      ]);

      let providers = [];
      let models = [];

      let xrtoken = null;
      if (provRes.code === 0) {
        providers = provRes.data?.list || provRes.data || [];
        setAllProviders(providers);
        xrtoken = providers.find(p =>
          p.name?.toLowerCase().includes('xrtoken') ||
          p.api_base?.toLowerCase().includes('xrtoken.ai') ||
          p.api_base?.toLowerCase().includes('xrtoken.net')
        );
        setXrProvider(xrtoken || null);
      }

      if (modelRes.code === 0) {
        models = modelRes.data?.list || modelRes.data || [];
        setAllModels(models);
      }

      // 过滤 XRToken provider 下的模型（用局部变量 xrtoken，避免 React 闭包旧值）
      const currentXRProvider = xrProvider || xrtoken; // xrtoken 是本轮查到的最新值
      const xrModels = models.filter(m =>
        currentXRProvider ? m.provider_id === currentXRProvider.id : false
      );
      // 没找到 provider 时回退到按名称过滤
      const xrModelsFallback = models.filter(m =>
        m.model_name?.toLowerCase().includes('seedance') ||
        m.model_name?.toLowerCase().includes('seedream')
      );
      const finalXRModels = xrModels.length > 0 ? xrModels : xrModelsFallback;
      console.log('[XRToken] xrModels:', xrModels.length, 'fallback:', xrModelsFallback.length, 'final:', finalXRModels.length);

      setSeedanceModels(finalXRModels.filter(m => m.type === 'video' || m.model_name?.toLowerCase().includes('seedance')));
      setSeedreamModels(finalXRModels.filter(m => m.type === 'image' || m.model_name?.toLowerCase().includes('seedream')));
    } catch (e) {
      console.error('Failed to fetch XRToken data:', e);
    } finally {
      setLoading(false);
    }
  }, [providerForm]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ========== 供应商 CRUD ==========
  const openCreateProvider = () => {
    setProviderEditMode(false);
    providerForm.resetFields();
    providerForm.setFieldsValue({
      name: 'xrtoken',
      display_name: 'XRToken',
      api_base: 'https://api.xrtoken.ai',
      status: 'active',
      adapter_type: 'openai',
      provider_type: 'internal',
    });
    setProviderModalOpen(true);
  };

  const openEditProvider = () => {
    if (!xrProvider) return;
    setProviderEditMode(true);
    setProviderModalOpen(true);
  };

  useEffect(() => {
    if (providerModalOpen && providerEditMode && xrProvider) {
      providerForm.setFieldsValue(xrProvider);
    }
  }, [providerModalOpen]);

  const handleProviderSave = async () => {
    try {
      const values = await providerForm.validateFields();
      setSaving(true);
      let res;
      if (providerEditMode && xrProvider) {
        res = await adminAPI.updateProvider(xrProvider.id, { ...xrProvider, ...values });
      } else {
        res = await adminAPI.createProvider(values);
      }
      if (res.code === 0) {
        message.success(providerEditMode ? '供应商更新成功' : '供应商创建成功');
        setProviderModalOpen(false);
        fetchData();
      } else {
        message.error(res.message || '操作失败');
      }
    } catch (e) {
      if (e.errorFields) return; // form validation
      message.error('操作失败: ' + (e.message || ''));
    } finally {
      setSaving(false);
    }
  };

  // ========== 模型 CRUD ==========
  const openCreateModel = (type) => {
    setModelEditMode(false);
    setEditingModel(null);
    setModelModalType(type);
    setFormBillingType(type === 'video' ? 'per_token' : 'per_image');
    setModelModalOpen(true);
  };

  const populateModelForm = (m) => {
    const billingType = m.billing_type || (m.model_type === 'video' ? 'per_token' : 'per_image');
    let metaStr = '';
    if (m.metadata) {
      try { const obj = typeof m.metadata === 'string' ? JSON.parse(m.metadata) : m.metadata; metaStr = JSON.stringify(obj, null, 2); } catch(e) { metaStr = typeof m.metadata === 'string' ? m.metadata : ''; }
    }
    modelForm.setFieldsValue({
      provider_id: m.provider_id || xrProvider?.id || 0,
      model_name: m.model_name, display_name: m.display_name,
      input_price: m.input_price, output_price: m.output_price || 0,
      api_path: m.api_path || '', status: m.status, priority: m.priority || 5,
      model_type: m.model_type || (m.model_name?.includes('seedance') ? 'video' : 'image'),
      upstream_model_name: m.upstream_model_name || '',
      billing_type: billingType, billing_rate: m.billing_rate || 0,
      metadata: metaStr,
    });
  };

  const openEditModel = (model) => {
    setModelEditMode(true);
    setEditingModel(model);
    setModelModalType(model.model_type || (model.model_name?.includes('seedance') ? 'video' : 'image'));
    setFormBillingType(model.billing_type || (model.model_type === 'video' ? 'per_token' : 'per_image'));
    setModelModalOpen(true);
  };

  // Modal打开后填充表单（等Form挂载完成，避免"not connected"警告）
  useEffect(() => {
    if (!modelModalOpen) return;
    if (editingModel) {
      populateModelForm(editingModel);
    } else {
      modelForm.resetFields();
      modelForm.setFieldsValue({
        model_type: modelModalType,
        provider_id: xrProvider?.id || 0,
        status: 'active',
        input_price: modelModalType === 'video' ? 7.0 : 0,
        output_price: 0,
        billing_type: modelModalType === 'video' ? 'per_token' : 'per_image',
        billing_rate: modelModalType === 'image' ? 0.03 : 0,
        priority: 5,
      });
    }
  }, [modelModalOpen]);

  const handleModelSave = async () => {
    try {
      const values = await modelForm.validateFields();
      setSaving(true);

      // 外部供应商：模型 ID 自动拼接为 供应商名/模型名
      let modelName = values.model_name;
      if (xrProvider?.provider_type === 'external' && modelName && !modelName.includes('/')) {
        modelName = `${xrProvider.name}/${modelName}`;
      }

      const data = {
        ...values,
        model_name: modelName,
        provider_id: xrProvider?.id || 0,
        provider_name: xrProvider?.name || 'xrtoken',
      };

      let res;
      if (modelEditMode && editingModel) {
        res = await adminAPI.updateModelConfig(editingModel.id, data);
      } else {
        res = await adminAPI.createModelConfig(data);
      }
      if (res.code === 0) {
        message.success(modelEditMode ? '模型更新成功' : '模型创建成功');
        setModelModalOpen(false);
        fetchData();
      } else {
        message.error(res.message || '操作失败');
      }
    } catch (e) {
      if (e.errorFields) return;
      message.error('操作失败: ' + (e.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModel = async (model) => {
    try {
      const res = await adminAPI.deleteModelConfig(model.id);
      if (res.code === 0) {
        message.success('删除成功');
        fetchData();
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (e) {
      message.error('删除失败: ' + (e.message || ''));
    }
  };

  const handleDeleteProvider = async () => {
    if (!xrProvider) return;
    try {
      const res = await adminAPI.deleteProvider(xrProvider.id);
      if (res.code === 0) {
        message.success('供应商已删除');
        setXrProvider(null);
        fetchData();
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (e) {
      message.error('删除失败: ' + (e.message || ''));
    }
  };

  // 一键创建默认模型
  const handleCreateDefaults = async () => {
    if (!xrProvider) {
      message.warning('请先创建 XRToken 供应商');
      return;
    }
    setSaving(true);
    let success = 0;
    let skipped = 0;
    for (const model of DEFAULT_MODELS) {
      const exists = allModels.find(m => m.model_name === model.model_name);
      if (exists) { skipped++; continue; }
      try {
        const res = await adminAPI.createModelConfig({
          ...model,
          provider_id: xrProvider.id,
          provider_name: xrProvider.name,
        });
        if (res.code === 0) success++;
      } catch (e) { /* skip */ }
    }
    setSaving(false);
    message.success(`创建成功 ${success} 个模型` + (skipped > 0 ? `，跳过 ${skipped} 个已存在的` : ''));
    fetchData();
  };

  // 从上游拉取模型列表
  const handleFetchUpstream = async () => {
    if (!xrProvider) {
      message.warning('请先创建 XRToken 供应商');
      return;
    }
    setUpstreamLoading(true);
    try {
      const res = await adminAPI.fetchUpstreamModels(xrProvider.api_base, xrProvider.api_key);
      if (res.code === 0) {
        // 过滤掉本地已存在的模型
        const existing = new Set(allModels.map(m => m.upstream_model_name || m.model_name));
        const newModels = (res.data || []).filter(m => !existing.has(m.id));
        setUpstreamModels(newModels);
        setSelectedUpstream([]);
        setUpstreamPrices({});
        if (newModels.length === 0) {
          message.info('所有上游模型已导入，无需同步');
        } else {
          setUpstreamModalOpen(true);
        }
      } else {
        message.error(res.message || '拉取失败');
      }
    } catch (e) {
      message.error('拉取上游模型失败: ' + (e.message || ''));
    } finally {
      setUpstreamLoading(false);
    }
  };

  // 导入选中的上游模型
  const handleImportUpstream = async () => {
    if (selectedUpstream.length === 0) {
      message.warning('请至少选择一个模型');
      return;
    }
    setSaving(true);
    let success = 0;
    for (const m of selectedUpstream) {
      const modelId = m.id; // e.g. "volcengine-overseas/dreamina-seedance-2-0-260128"
      const price = upstreamPrices[modelId] || (m.billing_type === 'per_image' ? 0.03 : 5.0);
      const isImage = m.type === 'image';
      try {
        const data = {
          model_name: modelId.split('/').pop() || modelId, // 简短名
          upstream_model_name: modelId,
          display_name: m.display_name || m.id,
          model_type: m.type,
          billing_type: isImage ? 'per_image' : 'per_token',
          input_price: isImage ? 0 : price,
          output_price: 0,
          billing_rate: isImage ? price : 0,
          api_path: m.type === 'video' ? '/videos/generations' : '/v1/images/generations',
          status: 'active',
          priority: 5,
          provider_id: xrProvider.id,
          provider_name: xrProvider.name,
        };
        const res = await adminAPI.createModelConfig(data);
        if (res.code === 0) success++;
      } catch (e) { /* skip */ }
    }
    setSaving(false);
    setUpstreamModalOpen(false);
    message.success(`导入成功 ${success} 个模型`);
    fetchData();
  };

  // ========== 表格列 ==========
  const modelColumns = [
    {
      title: '模型名称', dataIndex: 'model_name', key: 'model_name', width: 180,
      render: (name, record) => {
        const meta = parseModelMetadata(record);
        return (
          <Space direction="vertical" size={2}>
            <Space size={4}>
              <Text strong>{record.display_name || name}</Text>
              {meta?.tier && <Tag color={meta.tierColor} style={{ fontSize: 11 }}>{meta.tier}</Tag>}
            </Space>
            <Text type="secondary" style={{ fontSize: 11 }}>{name}</Text>
          </Space>
        );
      },
    },
    {
      title: '类型', dataIndex: 'model_name', key: 'type', width: 80,
      render: (name) => {
        const isVideo = name?.toLowerCase().includes('seedance');
        return <Tag color={isVideo ? 'blue' : 'purple'} icon={isVideo ? <VideoCameraOutlined /> : <PictureOutlined />}>{isVideo ? '视频' : '图片'}</Tag>;
      },
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 80,
      render: (status) => <Tag color={status === 'active' ? 'green' : 'red'}>{status === 'active' ? '启用' : '禁用'}</Tag>,
    },
    { title: '计费方式', dataIndex: 'billing_type', key: 'billing_type', width: 90, render: (v) => {
      if (v === 'per_image') return <Tag color="geekblue">按张</Tag>;
      if (v === 'per_token') return <Tag color="blue">按Token</Tag>;
      return v || '-';
    }},
    { title: '单价', key: 'unit_price', width: 100, render: (_, r) => {
      if (r.billing_type === 'per_image') return <span style={{fontWeight:600,color:'#ff4d4f'}}>${parseFloat(r.billing_rate||0).toFixed(3)}<small style={{color:'#888',fontWeight:400}}>/张</small></span>;
      if (r.billing_type === 'per_token') return <span style={{fontWeight:600,color:'#ff4d4f'}}>${parseFloat(r.input_price||0).toFixed(2)}<small style={{color:'#888',fontWeight:400}}>/M tokens</small></span>;
      return <span style={{fontWeight:600}}>${parseFloat(r.input_price||0).toFixed(2)}</span>;
    }},
    { title: 'API路径', dataIndex: 'api_path', key: 'api_path', width: 150, ellipsis: true },
    {
      title: '特性', dataIndex: 'metadata', key: 'features', width: 220,
      render: (_, record) => {
        const tags = getFeatureTags(record);
        if (tags.length === 0) return <Text type="secondary">未配置</Text>;
        return (
          <Space size={2} wrap>
            {tags.slice(0, 3).map((f, i) => (
              <Tag key={i} style={{ fontSize: 11, margin: '2px 0' }}>{f}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '操作', key: 'actions', width: 120, fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditModel(record)} />
          <Popconfirm title="确定删除此模型？" onConfirm={() => handleDeleteModel(record)} okText="删除" cancelText="取消">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ========== 渲染 ==========
  return (
    <div style={{ padding: '0 24px 24px', maxWidth: 1400 }}>
      {/* 页头 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Space align="center" size={12}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ApiOutlined style={{ fontSize: 20, color: '#fff' }} />
            </div>
            <div>
              <Title level={3} style={{ margin: 0 }}>XRToken 渠道配置</Title>
              <Space size={4}>
                {xrProvider ? <Tag color="green" icon={<CheckCircleOutlined />}>已接入</Tag> : <Tag color="orange" icon={<CloseCircleOutlined />}>未接入</Tag>}
                {seedanceModels.length + seedreamModels.length > 0 && <Tag color="blue">{seedanceModels.length + seedreamModels.length} 个模型</Tag>}
              </Space>
            </div>
          </Space>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchData}>刷新</Button>
        </Space>
      </div>

      {/* 说明 */}
      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        message="XRToken 渠道接入说明"
        description="XRToken 提供 Doubao Seedance 视频生成模型和 Seedream 图片生成模型。本页面一站式管理 XRToken 渠道的供应商配置和模型配置，无需前往其他页面。"
        style={{ marginBottom: 24 }}
      />

      {/* ======== 供应商配置 ======== */}
      <Card
        title={
          <Space>
            <KeyOutlined style={{ color: '#1677ff' }} />
            <span>供应商配置</span>
          </Space>
        }
        extra={
          <Space>
            {xrProvider ? (
              <>
                <Button size="small" icon={<EditOutlined />} onClick={openEditProvider}>编辑</Button>
                <Popconfirm title="确定删除此供应商？关联的模型将不可用。" onConfirm={handleDeleteProvider} okText="删除" cancelText="取消">
                  <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
                </Popconfirm>
              </>
            ) : (
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreateProvider}>创建供应商</Button>
            )}
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        {xrProvider ? (
          <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} bordered size="small">
            <Descriptions.Item label="供应商名称"><Text code>{xrProvider.name}</Text></Descriptions.Item>
            <Descriptions.Item label="显示名称">{xrProvider.display_name}</Descriptions.Item>
            <Descriptions.Item label="状态"><Tag color={xrProvider.status === 'active' ? 'green' : 'red'}>{xrProvider.status === 'active' ? '启用' : '禁用'}</Tag></Descriptions.Item>
            <Descriptions.Item label="API 地址" span={2}>
              <Space><LinkOutlined /> <Text code>{xrProvider.api_base}</Text></Space>
            </Descriptions.Item>
            <Descriptions.Item label="适配器">{xrProvider.adapter_type || 'openai'}</Descriptions.Item>
            <Descriptions.Item label="API Key" span={3}>
              <Space>
                <Text code>{xrProvider.api_key ? xrProvider.api_key.substring(0, 10) + '****' + xrProvider.api_key.slice(-4) : '未配置'}</Text>
              </Space>
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <KeyOutlined style={{ fontSize: 40, color: '#d9d9d9', marginBottom: 16 }} />
            <Paragraph type="secondary">
              尚未配置 XRToken 供应商。点击上方「创建供应商」按钮开始配置。
            </Paragraph>
            <Paragraph type="secondary" style={{ fontSize: 12 }}>
              API Base URL: <Text code>https://api.xrtoken.ai</Text>（海外）或 <Text code>https://api.xrtoken.net</Text>（国内）
            </Paragraph>
          </div>
        )}
      </Card>

      {/* ======== Seedance 视频模型 ======== */}
      <Card
        title={
          <Space>
            <VideoCameraOutlined style={{ color: '#1677ff' }} />
            <span>Seedance 视频模型</span>
            <Tag color="blue">{seedanceModels.length}</Tag>
          </Space>
        }
        extra={
          <Space>
            {!xrProvider && <Text type="secondary" style={{ fontSize: 12 }}>请先创建供应商</Text>}
            <Button type="primary" size="small" icon={<PlusOutlined />} disabled={!xrProvider} onClick={() => openCreateModel('video')}>添加视频模型</Button>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Table
          columns={modelColumns}
          dataSource={seedanceModels}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={false}
          locale={{ emptyText: '暂无 Seedance 视频模型' }}
        />
      </Card>

      {/* ======== Seedream 图片模型 ======== */}
      <Card
        title={
          <Space>
            <PictureOutlined style={{ color: '#722ed1' }} />
            <span>Seedream 图片模型</span>
            <Tag color="purple">{seedreamModels.length}</Tag>
          </Space>
        }
        extra={
          <Button type="primary" size="small" icon={<PlusOutlined />} disabled={!xrProvider} onClick={() => openCreateModel('image')}>添加图片模型</Button>
        }
      >
        <Table
          columns={modelColumns}
          dataSource={seedreamModels}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={false}
          locale={{ emptyText: '暂无 Seedream 图片模型' }}
        />
      </Card>

      {/* ======== Provider 编辑弹窗 ======== */}
      <Modal
        title={providerEditMode ? '编辑 XRToken 供应商' : '创建 XRToken 供应商'}
        open={providerModalOpen}
        onCancel={() => setProviderModalOpen(false)}
        onOk={handleProviderSave}
        confirmLoading={saving}
        width={560}
        destroyOnHidden
      >
        <Form form={providerForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="供应商名称" rules={[{ required: true, message: '请输入供应商名称' }]}>
            <Input placeholder="xrtoken" />
          </Form.Item>
          <Form.Item name="display_name" label="显示名称" rules={[{ required: true, message: '请输入显示名称' }]}>
            <Input placeholder="XRToken" />
          </Form.Item>
          <Form.Item name="api_base" label="API Base URL" rules={[{ required: true, message: '请输入 API 地址' }]}>
            <Input placeholder="https://api.xrtoken.ai" />
          </Form.Item>
          <Form.Item name="api_key" label="API Key" rules={[{ required: true, message: '请输入 API Key' }]}>
            <Input.Password placeholder="tr-..." />
          </Form.Item>
          <Form.Item name="adapter_type" label="适配器类型">
            <Select options={[
              { value: 'openai', label: 'OpenAI 兼容' },
              { value: 'custom', label: '自定义' },
            ]} />
          </Form.Item>
          <Form.Item name="provider_type" label="供应商类型" extra="外部供应商的模型 ID = 供应商名/模型名，用于区分同名模型">
            <Select options={[
              { value: 'internal', label: '内部供应商（默认）' },
              { value: 'external', label: '外部供应商（模型ID: 供应商名/模型名）' },
            ]} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={[
              { value: 'active', label: '启用' },
              { value: 'inactive', label: '禁用' },
            ]} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ======== Model 编辑弹窗 ======== */}
      <Modal
        title={modelEditMode ? '编辑模型配置' : `添加 ${modelModalType === 'video' ? '视频' : '图片'}模型`}
        open={modelModalOpen}
        onCancel={() => setModelModalOpen(false)}
        onOk={handleModelSave}
        confirmLoading={saving}
        width={640}
      >
        <Form form={modelForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="model_name" label="模型名称 (model)" rules={[{ required: true, message: '请输入模型名称' }]}
            extra={xrProvider?.provider_type === 'external' ? `外部供应商，将自动拼接为: ${xrProvider?.name || 'provider'}/模型名` : '内部供应商，直接使用模型名即可'}>
            <Input placeholder={modelModalType === 'video' ? 'seedance-2.0' : 'seedream-4.0'} />
          </Form.Item>
          <Form.Item name="display_name" label="显示名称" rules={[{ required: true, message: '请输入显示名称' }]}>
            <Input placeholder={modelModalType === 'video' ? 'Seedance 2.0 旗舰版' : 'Seedream 4.0 旗舰版'} />
          </Form.Item>
          <Form.Item name="model_type" label="模型类型" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="provider_id" label="供应商ID" hidden>
            <Input />
          </Form.Item>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="priority" label="优先级">
              <InputNumber min={0} max={100} style={{ width: 100 }} placeholder="5" />
            </Form.Item>
            <Form.Item name="billing_type" label="计费方式">
              <Select options={[
                { value: 'per_token', label: '按 token（视频）' },
                { value: 'per_image', label: '按张（图片）' },
                { value: 'per_call', label: '按次（固定）' },
              ]} style={{ width: 150 }} onChange={(v) => setFormBillingType(v)} />
            </Form.Item>
          </Space>
          {formBillingType === 'per_token' && (
            <Space size={16} style={{ width: '100%' }}>
              <Form.Item name="input_price" label="单价（$/百万tokens）" rules={[{ required: true }]}>
                <InputNumber min={0} step={0.01} style={{ width: 200 }} placeholder="7.0" />
              </Form.Item>
              <Form.Item name="output_price" label="输出价格（$）" hidden>
                <InputNumber min={0} step={0.01} style={{ width: 160 }} value={0} />
              </Form.Item>
            </Space>
          )}
          {(formBillingType === 'per_image' || formBillingType === 'per_call') && (
            <Space size={16} style={{ width: '100%' }}>
              <Form.Item name="billing_rate" label={formBillingType === 'per_image' ? '单价（$/张）' : '单价（$/次）'} rules={[{ required: true }]}>
                <InputNumber min={0} step={0.001} style={{ width: 200 }} placeholder={formBillingType === 'per_image' ? '0.03' : '0.5'} />
              </Form.Item>
              <Form.Item name="input_price" hidden>
                <InputNumber min={0} step={0.01} value={0} />
              </Form.Item>
              <Form.Item name="output_price" hidden>
                <InputNumber min={0} step={0.01} value={0} />
              </Form.Item>
            </Space>
          )}
          <Form.Item name="upstream_model_name" label="上游模型名" extra="上游实际使用的模型 ID，留空则使用 model_name">
            <Input placeholder="如: dreamina-seedance-2-0-260128" />
          </Form.Item>
          <Form.Item name="api_path" label="API 路径">
            <Input placeholder={modelModalType === 'video' ? '/videos/generations' : '/v1/images/generations'} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={[
              { value: 'active', label: '启用' },
              { value: 'inactive', label: '禁用' },
            ]} />
          </Form.Item>

          {/* ========== 特性/元数据编辑（可折叠） ========== */}
          <Divider plain style={{ fontSize: 13, color: '#888' }}>模型特性（Metadata / 特性标签）</Divider>

          <Form.Item name="metadata" label="元数据 JSON" extra="编辑模型的特性、等级、描述、分辨率、风格等信息。保存 JSON 格式。">
            <Input.TextArea
              rows={modelEditMode ? 12 : 8}
              placeholder={`{${'\n'}  "version": "${modelModalType === 'video' ? '2.0' : '4.0'}",${'\n'}  "tier": "旗舰版",${'\n'}  "tierColor": "#ff4d4f",${'\n'}  "desc": "模型描述说明",${'\n'}  "features": ["特性1", "特性2", "特性3"]${'\n'}}`}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Form.Item>
          <Form.Item name="transform_config" label="格式转换配置" extra="请求/响应模板转换（用于新模型 API 格式兼容）。Seedance 2.5 必填。">
            <Input.TextArea
              rows={4}
              placeholder='{"request_transform":{"body_template":{"model":"{UPSTREAM_MODEL}","content":[{"type":"text","text":"{prompt}"}],"ratio":"{ratio}"}}}'
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Form.Item>

          <Space style={{ marginTop: -16, marginBottom: 12 }}>
            <Button size="small" type="link" style={{ padding: 0 }} onClick={() => {
              const val = modelForm.getFieldValue('metadata');
              if (!val) return;
              try { modelForm.setFieldsValue({ metadata: JSON.stringify(JSON.parse(val), null, 2) }); message.success('JSON 已格式化'); } catch(e) { message.error('JSON 格式错误'); }
            }}>格式化 Metadata JSON</Button>
            <Button size="small" type="link" style={{ padding: 0 }} onClick={() => {
              // 能力声明式：新增一个支持图层拆分的模型（例如下个版本或上游开放后的 Flash），
              // 只需点这里插入模板，前后端就自动出现开关并校验参数，不用改代码。
              const template = {
                enabled: true,
                ui: { label: '图层拆分', labelEn: 'Layer Decomposition', hint: '把一张图拆成底图 + 最多 16 个透明图层，按实际输出张数计费（分层输出为普通价的 5 折）' },
                input: { count: 1, formats: ['png', 'jpeg'], required: true },
                output: { max_layers: 16, base_index: 0, layer_format: 'png', return_fields: ['z_index', 'name', 'description', 'bounding_box', 'size', 'output_format'] },
                params: { n: { fixed: 1 }, size: { allowed: ['auto', '1K', '1.5K', '2K'], default: 'auto' }, prompt: { required: false } },
                billing: { mode: 'per_output', max_outputs: 17, pre_deduct: 'max_tier_x_max_outputs', input_images_free: 1, price_multiplier: 0.5 },
                force_response_format: 'url',
              };
              let meta = {};
              try {
                const val = modelForm.getFieldValue('metadata');
                meta = val ? JSON.parse(val) : {};
              } catch (e) {
                message.error('当前 Metadata 不是合法 JSON，先修好再插入');
                return;
              }
              meta.capabilities = { ...(meta.capabilities || {}), layer_decomposition: template };
              modelForm.setFieldsValue({ metadata: JSON.stringify(meta, null, 2) });
              message.success('已插入图层拆分能力模板');
            }}>+ 插入图层拆分能力</Button>
          </Space>

          <Alert
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            message="Metadata 字段说明"
            description={
              <div style={{ fontSize: 11, maxHeight: 120, overflow: 'auto' }}>
                <div><Text code>features</Text>: 特性标签数组</div>
                <div><Text code>tier</Text> / <Text code>tierColor</Text>: 等级名称和颜色（如 "旗舰版"/"#ff4d4f"）</div>
                <div><Text code>desc</Text>: 模型描述</div>
                <div>视频额外: <Text code>resolutions</Text> (分辨率为对象数组), <Text code>durationRange</Text>, <Text code>fpsOptions</Text></div>
                <div>图片额外: <Text code>sizes</Text> (尺寸为对象数组), <Text code>styles</Text> (风格为对象数组), <Text code>maxImages</Text></div>
              </div>
            }
            style={{ marginTop: -8, fontSize: 11 }}
          />
        </Form>
      </Modal>

      {/* ========== 从上游同步模型弹窗 ========== */}
      <Modal
        title="从 XRToken 上游同步模型"
        open={upstreamModalOpen}
        onCancel={() => setUpstreamModalOpen(false)}
        onOk={handleImportUpstream}
        confirmLoading={saving}
        okText={`导入选中 (${selectedUpstream.length})`}
        width={700}
        destroyOnHidden
      >
        {upstreamModels.length === 0 ? (
          <Text type="secondary">暂无可导入的新模型</Text>
        ) : (
          <>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
              以下模型来自 XRToken 上游，选择需要导入的模型并设置价格。已导入的模型不显示。
            </Text>
            <div style={{ maxHeight: '50vh', overflow: 'auto' }}>
              {upstreamModels.map(m => {
                const isVideo = m.type === 'video';
                const modelId = m.id;
                const checked = selectedUpstream.some(s => s.id === modelId);
                const defaultPrice = m.billing_type === 'per_image' ? 0.03 : 5.0;
                return (
                  <div key={modelId} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '8px 12px', borderBottom: '1px solid #f0f0f0',
                    background: checked ? '#f6ffed' : '#fff',
                  }}>
                    <input type="checkbox" checked={checked} onChange={() => {
                      setSelectedUpstream(prev => {
                        if (checked) return prev.filter(s => s.id !== modelId);
                        return [...prev, m];
                      });
                      if (!checked && !upstreamPrices[modelId]) {
                        setUpstreamPrices(prev => ({ ...prev, [modelId]: defaultPrice }));
                      }
                    }} style={{ width: 16, height: 16 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{m.display_name}</div>
                      <div style={{ fontSize: 11, color: '#888', fontFamily: 'monospace' }}>{modelId}</div>
                      <Space size={4}>
                        <Tag color={isVideo ? 'blue' : 'purple'} style={{ fontSize: 10 }}>{isVideo ? '视频' : '图片'}</Tag>
                        <Tag style={{ fontSize: 10 }}>{m.billing_type === 'per_image' ? '按张计费' : '按token计费'}</Tag>
                      </Space>
                    </div>
                    <div style={{ width: 120 }}>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                        {isVideo ? '价格 ($/M tokens)' : '价格 ($/张)'}
                      </Text>
                      <Input
                        size="small"
                        type="number"
                        min={0.001}
                        step={0.001}
                        value={upstreamPrices[modelId] || defaultPrice}
                        onChange={e => setUpstreamPrices(prev => ({ ...prev, [modelId]: parseFloat(e.target.value) || 0 }))}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default XRToken;
